/**
 * Panel 4 — `Umpires Appointments` (§B8), the per-umpire summary of the TMS-actual half.
 *
 * Three figures, never one. §B8 gives the table `Name · Country · On-Field · Video · Reserve`
 * and **no total column**, for §B6's reason: summing a video load into an on-field load hides
 * the imbalance the page exists to reveal. `UmpireSummaryRow` therefore carries three counts
 * and nothing that could be printed as a fourth.
 *
 * The `Avoids Video` marker in the Video column is the one addition to §B8's own columns, and
 * it is the Run 33 field doing the job it was added for: an umpire at `0 / 0` video because
 * the Technical Delegate took them out of the video pool is not the same finding as an umpire
 * at `0 / 0` because the Umpires Managers have overlooked them, and this table is read to tell
 * exactly those two apart.
 */

import { useEffect, useState } from "react";

import { Tabs } from "../appointments/Tabs.js";
import { cellClass } from "../appointments/useCellFlash.js";
import type { UmpireCount, UmpireGenderFilter, UmpireSummaryRow } from "./derive.js";

/** §B8: page size 8. */
const PAGE = 8;

/** §B8's own filter bar — gender, and nothing else. */
export const GENDER_TABS = [
  { value: "all", label: "All" },
  { value: "m", label: "Male" },
  { value: "f", label: "Female" },
] as const;

export interface UmpireSummaryPanelProps {
  rows: readonly UmpireSummaryRow[];
  gender: UmpireGenderFilter;
  onGender: (value: UmpireGenderFilter) => void;
  flashing: ReadonlySet<string>;
  /** False where the schedule could not be read, so `completed` is unmeasured rather than nil. */
  scheduleLoaded: boolean;
}

function Count({
  count,
  testId,
  flashKey,
  flashing,
}: {
  count: UmpireCount;
  testId: string;
  flashKey: string;
  flashing: ReadonlySet<string>;
}): React.JSX.Element {
  const zero = count.appointed === 0 && count.completed === 0;
  return (
    <td
      className={cellClass(`cell-cnt${zero ? " z" : ""}`, flashKey, flashing)}
      data-testid={testId}
    >
      {count.completed}/{count.appointed}
    </td>
  );
}

export function UmpireSummaryPanel({
  rows,
  gender,
  onGender,
  flashing,
  scheduleLoaded,
}: UmpireSummaryPanelProps): React.JSX.Element {
  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));

  useEffect(() => {
    setPage((current) => Math.min(current, pages - 1));
  }, [pages]);

  const shown = rows.slice(page * PAGE, page * PAGE + PAGE);

  return (
    <section className="panel panel--d4" aria-labelledby="umpSumTitle" data-testid="umpire-summary">
      <div className="panel__head">
        <h3 className="panel__title" id="umpSumTitle">
          Umpires Appointments
        </h3>
        <span className="spacer" />
        <Tabs
          label="Umpires gender filter"
          options={GENDER_TABS}
          value={gender}
          onChange={(value) => {
            setPage(0);
            onGender(value);
          }}
        />
      </div>

      <div className="readonlycap">
        <span className="lock" aria-hidden="true">
          🔒
        </span>
        Read-Only — Appointed By The Umpires Managers, As Recorded In This System
      </div>

      <table className="mtable sumtable" aria-label="Umpires appointment counts">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Country</th>
            <th scope="col">On-Field</th>
            <th scope="col">Video</th>
            <th scope="col">Reserve</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((row) => {
            const id = row.umpire.tmsPersonId;
            return (
              <tr
                key={id}
                className={row.umpire.gender === null ? undefined : `g-${row.umpire.gender}`}
                data-testid={`umpire-row-${id}`}
              >
                <td className="cell-name">{row.umpire.mediaName}</td>
                <td className="cell-ctry">{row.umpire.country}</td>
                <Count
                  count={row.onField}
                  testId={`umpire-onfield-${id}`}
                  flashKey={`as:${id}:on`}
                  flashing={flashing}
                />
                <td
                  className={cellClass(
                    `cell-cnt${row.video.appointed === 0 && row.video.completed === 0 ? " z" : ""}`,
                    `as:${id}:vu`,
                    flashing,
                  )}
                  data-testid={`umpire-video-${id}`}
                >
                  {row.video.completed}/{row.video.appointed}
                  {row.umpire.avoidVu ? (
                    <span className="avoidmark" data-testid={`umpire-avoidvu-${id}`}>
                      Avoids Video
                    </span>
                  ) : null}
                </td>
                <Count
                  count={row.reserve}
                  testId={`umpire-reserve-${id}`}
                  flashKey={`as:${id}:ru`}
                  flashing={flashing}
                />
              </tr>
            );
          })}
        </tbody>
      </table>

      {rows.length === 0 ? (
        <div className="emptystate">
          <b>No umpire duties to summarise yet.</b>
          The three figures appear once the appointments are loaded.
        </div>
      ) : null}

      <div className="pager" aria-label="Umpires pages">
        <span className="pager__info num" data-testid="umpire-pager">
          Page {page + 1} Of {pages} · {rows.length} Umpires
        </span>
        <button
          type="button"
          disabled={page === 0}
          onClick={() => {
            setPage((current) => Math.max(0, current - 1));
          }}
        >
          ‹ Prev
        </button>
        <button
          type="button"
          disabled={page >= pages - 1}
          onClick={() => {
            setPage((current) => Math.min(pages - 1, current + 1));
          }}
        >
          Next ›
        </button>
      </div>

      <div className="panel__foot">
        <b>Three figures, never one —</b> <b>On-Field</b> is U1 + U2, <b>Video</b> is VU and{" "}
        <b>Reserve</b> is RU, each as completed / appointed. They are shown separately and
        deliberately never summed. Sorted by on-field appointed, then completed.
        <div className="hidenote">
          {scheduleLoaded ? (
            <>
              <b>Completed is read from the schedule —</b> a duty counts as completed only where
              the schedule recorded in this system reports the match itself as Completed or
              Official. Nothing here is inferred from the clock.
            </>
          ) : (
            <>
              <b>Completed is unmeasured in this view —</b> the schedule could not be read, and
              a match&rsquo;s recorded status is the only thing that says it has been played.
              Every completed figure is therefore shown as nil rather than guessed.
            </>
          )}
        </div>
      </div>
    </section>
  );
}
